import { Component, Output, EventEmitter, Input, Signal, signal, inject } from '@angular/core';
import { CoreService } from 'src/app/shared/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { navItems } from '../../vertical/sidebar/sidebar-data';
import { TranslateService } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { BrandingComponent } from '../../vertical/sidebar/branding.component';
import { AppSettings } from 'src/app/config';
import { FormsModule } from '@angular/forms';
import { IDataUser } from 'src/app/apps/auth/interfaces/ILoginEmployee';
import { CommonModule } from '@angular/common';
import { PaquetesDocumentalesMonitorService } from 'src/app/shared/services/paquetes-documentales-monitor.service';
import { FacturacionPendienteMonitorService } from 'src/app/shared/services/facturacion-pendiente-monitor.service';
import { IFacturacionPendiente } from 'src/app/apps/nodo/modules/facturacion-pendiente/interfaces/IFacturacionPendiente';
import { IPaqueteDocumental } from 'src/app/apps/nodo/modules/paquetes-documentales/interfaces/paquete-documental';

interface notifications {
  id: number;
  icon: string;
  color: string;
  title: string;
  time: string;
  subtitle: string;
}

interface profiledd {
  id: number;
  title: string;
  link: string;
  new?: boolean;
}

interface apps {
  id: number;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  link: string;
}

@Component({
  selector: 'app-horizontal-header',
  imports: [RouterModule, CommonModule, TablerIconsModule, MaterialModule, BrandingComponent],
  templateUrl: './header.component.html',
  styles: [
    `
      .paquetes-bell-badge {
        position: absolute;
        top: 4px;
        right: 4px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        border-radius: 8px;
        background: #ef4444;
        color: white;
        font-size: 10px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
    `
  ]
})
export class AppHorizontalHeaderComponent {
  private readonly router = inject(Router);
  // El servicio de monitor se inyecta aquí para garantizar que el singleton
  // esté activo mientras este topbar esté montado (portal Nodo, layout horizontal).
  readonly _paquetesMonitor = inject(PaquetesDocumentalesMonitorService);
  readonly paquetesPendientes = this._paquetesMonitor.pendientes;

  // Facturación pendiente de aprobación (AIU dinámico, HAG-152).
  readonly _facturacionMonitor = inject(FacturacionPendienteMonitorService);
  readonly facturacionPendientes = this._facturacionMonitor.pendientes;

  /** Etiqueta corta del caso de facturación: cliente + corte. */
  etiquetaFacturacion(c: IFacturacionPendiente): string {
    const cliente = c.clienteNombre?.trim() || c.cliente;
    const corte = (c.corte ?? '').slice(0, 10);
    return corte ? `${cliente} · corte ${corte}` : cliente;
  }

  /** Clic en un aviso de facturación: marca visto y abre la pantalla. */
  openFacturacionPendienteNotif(c: IFacturacionPendiente): void {
    this._facturacionMonitor.marcarVisto(c.id);
    this.router.navigate(['/app/nodo/nomina/facturacion-pendiente']);
  }

  /**
   * Etiqueta del periodo de un paquete. Duplicada respecto a la pantalla porque
   * los headers no dependen de ese módulo; el formato debe coincidir con el que
   * usa el backend para nombrar el archivo.
   */
  periodoPaquete(p: IPaqueteDocumental): string {
    const lista = (p.meses ?? '')
      .split(',')
      .map((m) => Number(m.trim()))
      .filter((m) => m >= 1 && m <= 12);

    if (lista.length === 1) return `${p.anio}-${String(lista[0]).padStart(2, '0')}`;
    if (lista.length > 1) return `${p.anio} (${lista.length} meses)`;
    return `${p.anio}`;
  }


  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  userData: Signal<IDataUser | null>;
  nameInitials: string = '';

  showFiller = false;

  @Output() optionsChange = new EventEmitter<AppSettings>();

  public selectedLanguage: any = {
    language: 'English',
    code: 'en',
    type: 'US',
    icon: '/assets/images/flag/icon-flag-en.svg'
  };

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg'
    },
    {
      language: 'Español',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg'
    },
    {
      language: 'Français',
      code: 'fr',
      icon: '/assets/images/flag/icon-flag-fr.svg'
    },
    {
      language: 'German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.svg'
    }
  ];

  constructor(
    private settings: CoreService,
    private vsidenav: CoreService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    translate.setDefaultLang('en');
    this.getDataUser();
  }

  getDataUser() {
    const userData = this.settings.getUserData();
    if (userData) {
      this.userData = signal(userData);
      this.nameInitials = userData.nombre
        ? userData.nombre
            .split(' ')
            .map((n: string) => n[0])
            .filter((_: string, i: number) => i < 2)
            .join('')
            .toUpperCase()
        : '';
    } else {
      this.userData = signal(null);
    }
  }

  openDialog() {
    const dialogRef = this.dialog.open(AppHorizontalSearchDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      // console.log(`Dialog result: ${result}`);
    });
  }

  changeLanguage(lang: any): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
  }

  /**
   * Al hacer clic en un aviso de paquete documental, navega a la pantalla y lo
   * quita de la lista local. Si todavía no se descargó, el próximo sondeo lo
   * vuelve a traer: la fuente de verdad es el backend, no este descarte local.
   */
  openPaqueteDocumentalNotif(p: IPaqueteDocumental): void {
    this._paquetesMonitor.marcarVisto(p.id);
    this.router.navigate(['/app/nodo/juridica/paquetes-documentales']);
  }

  options = this.settings.getOptions();

  private emitOptions() {
    this.optionsChange.emit(this.options);
  }

  setlightDark(theme: string) {
    this.options.theme = theme;
    this.emitOptions();
  }

  notifications: notifications[] = [
    {
      id: 1,
      icon: 'a-b-2',
      color: 'primary',
      time: '8:30 AM',
      title: 'Launch Admin',
      subtitle: 'Just see the my new admin!'
    },
    {
      id: 2,
      icon: 'calendar',
      color: 'secondary',
      time: '8:21 AM',
      title: 'Event today',
      subtitle: 'Just a reminder that you have event'
    },
    {
      id: 3,
      icon: 'settings',
      color: 'warning',
      time: '8:05 AM',
      title: 'Settings',
      subtitle: 'You can customize this template'
    },
    {
      id: 4,
      icon: 'a-b-2',
      color: 'success',
      time: '7:30 AM',
      title: 'Launch Templates',
      subtitle: 'Just see the my new admin!'
    },
    {
      id: 5,
      icon: 'exclamation-circle',
      color: 'error',
      time: '7:03 AM',
      title: 'Event tomorrow',
      subtitle: 'Just a reminder that you have event'
    }
  ];

  profiledd: profiledd[] = [
    // {
    //   id: 1,
    //   title: 'My Profile',
    //   link: '/',
    // },
    // {
    //   id: 2,
    //   title: 'My Subscription',
    //   link: '/',
    // },
    // {
    //   id: 3,
    //   title: 'My Invoice',
    //   new: true,
    //   link: '/',
    // },
    // {
    //   id: 4,
    //   title: ' Account Settings',
    //   link: '/',
    // },
    {
      id: 5,
      title: 'Cerrar sesión',
      link: '/login'
    }
  ];

  apps: apps[] = [
    {
      id: 1,
      icon: 'solar:chat-line-line-duotone',
      color: 'primary',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/'
    },
    {
      id: 2,
      icon: 'solar:checklist-minimalistic-line-duotone',
      color: 'secondary',
      title: 'Todo App',
      subtitle: 'Completed task',
      link: '/'
    },
    {
      id: 3,
      icon: 'solar:bill-list-line-duotone',
      color: 'success',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/'
    },
    {
      id: 4,
      icon: 'solar:calendar-line-duotone',
      color: 'error',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/'
    },
    {
      id: 5,
      icon: 'solar:smartphone-2-line-duotone',
      color: 'warning',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/'
    },
    {
      id: 6,
      icon: 'solar:ticket-line-duotone',
      color: 'primary',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/'
    },
    {
      id: 7,
      icon: 'solar:letter-line-duotone',
      color: 'secondary',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/'
    },
    {
      id: 8,
      icon: 'solar:book-2-line-duotone',
      color: 'warning',
      title: 'Contact List',
      subtitle: 'Create new contact',
      link: '/'
    }
  ];
}

@Component({
  selector: 'app-search-dialog',
  imports: [RouterModule, MaterialModule, TablerIconsModule, FormsModule],
  templateUrl: 'search-dialog.component.html'
})
export class AppHorizontalSearchDialogComponent {
  searchText: string = '';
  navItems = navItems;

  navItemsData = navItems.filter((navitem) => navitem.displayName);

  // filtered = this.navItemsData.find((obj) => {
  //   return obj.displayName == this.searchinput;
  // });
}
